import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Heart, List, Map as MapIcon, Search, SlidersHorizontal, Columns2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatPrice } from "@/lib/api";
import { CATEGORIES, AREA_UNITS, PRICE_STEPS, isLandCategory, shortPrice } from "./searchShared";

function ToolbarButton({ label, active, children, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={`inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-3.5 text-[13px] font-bold transition-colors ${active ? "border-primary/50 bg-primary/5 text-primary" : "border-border bg-background text-foreground hover:border-foreground/30"}`}
        >
          {label}
          <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={8} className="w-72 rounded-xl p-4 shadow-xl">
        {typeof children === "function" ? children(() => setOpen(false)) : children}
      </PopoverContent>
    </Popover>
  );
}

function PopoverApply({ onApply, close }) {
  return (
    <Button type="button" size="sm" className="mt-3 w-full rounded-full font-bold" onClick={() => { onApply(); close(); }}>
      Apply
    </Button>
  );
}

export default function SearchFilterToolbar({
  params,
  applyParams,
  drawerFilterCount,
  onOpenFilters,
  view,
  onViewChange,
  showViewToggle = true,
}) {
  const [locationText, setLocationText] = useState(params.q || params.city || "");
  const [minPrice, setMinPrice] = useState(params.min_price || "");
  const [maxPrice, setMaxPrice] = useState(params.max_price || "");
  const [minArea, setMinArea] = useState(params.min_area || "");
  const [maxArea, setMaxArea] = useState(params.max_area || "");
  const [areaUnit, setAreaUnit] = useState(params.area_unit || "");
  const skipLocationSync = useRef(false);

  useEffect(() => {
    if (skipLocationSync.current) { skipLocationSync.current = false; return; }
    setLocationText(params.q || params.city || "");
    setMinPrice(params.min_price || "");
    setMaxPrice(params.max_price || "");
    setMinArea(params.min_area || "");
    setMaxArea(params.max_area || "");
    setAreaUnit(params.area_unit || "");
  }, [params.q, params.city, params.min_price, params.max_price, params.min_area, params.max_area, params.area_unit]);

  const land = isLandCategory(params.category);
  const listingLabel = params.listing_type === "RENT" ? "For Rent" : "For Sale";
  const categoryLabel = CATEGORIES.find((c) => c.v === params.category)?.l || "All Types";
  const priceLabel = params.min_price || params.max_price
    ? `${params.min_price ? shortPrice(params.min_price) : "₹0"} – ${params.max_price ? shortPrice(params.max_price) : "Any"}`
    : "Price";
  const sizeLabel = land
    ? (params.min_area || params.max_area ? `${params.min_area || 0}–${params.max_area || "Any"}` : "Plot Size")
    : (params.bedrooms ? `${params.bedrooms}+ BHK` : "Beds");

  const submitLocation = (event) => {
    event.preventDefault();
    skipLocationSync.current = true;
    const text = locationText.trim();
    // Free-text search goes through q; clearing removes both q and city.
    applyParams({ q: text || "", city: "", near_lat: "", near_lng: "", radius_km: "" });
  };

  const saveSearch = () => {
    try {
      const entry = { url: `${window.location.pathname}${window.location.search}`, saved_at: new Date().toISOString() };
      const list = JSON.parse(localStorage.getItem("saved_searches") || "[]").filter((s) => s.url !== entry.url);
      list.unshift(entry);
      localStorage.setItem("saved_searches", JSON.stringify(list.slice(0, 20)));
      toast.success("Search saved on this device");
    } catch {
      toast.error("Search could not be saved");
    }
  };

  const viewOptions = [
    { v: "split", l: "Split", icon: Columns2 },
    { v: "list", l: "List", icon: List },
    { v: "map", l: "Map", icon: MapIcon },
  ];

  return (
    <div className="sticky top-16 z-30 border-b border-border bg-background/95 backdrop-blur" data-testid="search-filter-toolbar">
      <div className="mx-auto flex max-w-[1700px] items-center gap-2 overflow-x-auto px-3 py-2.5 sm:px-5 scrollbar-none">
        <form onSubmit={submitLocation} className="relative w-44 shrink-0 sm:w-60" data-testid="toolbar-location-form">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={locationText}
            onChange={(event) => setLocationText(event.target.value)}
            placeholder="City, locality, landmark"
            className="h-9 rounded-full pl-9 text-[13px] font-semibold"
            data-testid="toolbar-location-input"
          />
        </form>

        <ToolbarButton label={listingLabel} active={!!params.listing_type} testId="toolbar-listing-type">
          {(close) => (
            <div className="space-y-1.5">
              <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Listing status</p>
              {[{ v: "", l: "For Sale" }, { v: "RENT", l: "For Rent" }].map((option) => (
                <button
                  key={option.l}
                  type="button"
                  onClick={() => { applyParams({ listing_type: option.v }); close(); }}
                  className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-sm font-semibold ${(params.listing_type || "") === option.v ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-foreground/30"}`}
                >
                  {option.l}
                </button>
              ))}
            </div>
          )}
        </ToolbarButton>

        <ToolbarButton label={categoryLabel} active={!!params.category} testId="toolbar-category">
          {(close) => (
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Property type</p>
              <div className="mt-2 grid grid-cols-2 gap-1.5">
                <button type="button" onClick={() => { applyParams({ category: "" }); close(); }} className={`rounded-lg border px-3 py-2 text-sm font-semibold ${!params.category ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-foreground/30"}`}>All Types</button>
                {CATEGORIES.map((c) => (
                  <button key={c.v} type="button" onClick={() => { applyParams({ category: c.v }); close(); }} className={`rounded-lg border px-3 py-2 text-sm font-semibold ${params.category === c.v ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-foreground/30"}`}>{c.l}</button>
                ))}
              </div>
            </div>
          )}
        </ToolbarButton>

        <ToolbarButton label={priceLabel} active={!!(params.min_price || params.max_price)} testId="toolbar-price">
          {(close) => (
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Price range</p>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <select value={minPrice} onChange={(event) => setMinPrice(event.target.value)} className="h-9 rounded-lg border border-border bg-background px-2 text-sm font-semibold" aria-label="Minimum price">
                  <option value="">No min</option>
                  {PRICE_STEPS.map((v) => <option key={v} value={v}>{formatPrice(v)}</option>)}
                </select>
                <select value={maxPrice} onChange={(event) => setMaxPrice(event.target.value)} className="h-9 rounded-lg border border-border bg-background px-2 text-sm font-semibold" aria-label="Maximum price">
                  <option value="">No max</option>
                  {PRICE_STEPS.map((v) => <option key={v} value={v}>{formatPrice(v)}</option>)}
                </select>
              </div>
              <PopoverApply close={close} onApply={() => applyParams({ min_price: minPrice, max_price: maxPrice })} />
            </div>
          )}
        </ToolbarButton>

        <ToolbarButton label={sizeLabel} active={land ? !!(params.min_area || params.max_area) : !!params.bedrooms} testId="toolbar-size">
          {(close) => land ? (
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Plot size</p>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <Input type="number" min="0" placeholder="Min" value={minArea} onChange={(event) => setMinArea(event.target.value)} className="h-9" aria-label="Minimum area" />
                <Input type="number" min="0" placeholder="Max" value={maxArea} onChange={(event) => setMaxArea(event.target.value)} className="h-9" aria-label="Maximum area" />
              </div>
              <select value={areaUnit} onChange={(event) => setAreaUnit(event.target.value)} className="mt-2 h-9 w-full rounded-lg border border-border bg-background px-2 text-sm font-semibold" aria-label="Area unit">
                <option value="">Any unit</option>
                {AREA_UNITS.map((u) => <option key={u.v} value={u.v}>{u.l}</option>)}
              </select>
              <PopoverApply close={close} onApply={() => applyParams({ min_area: minArea, max_area: maxArea, area_unit: areaUnit })} />
            </div>
          ) : (
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Bedrooms</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => { applyParams({ bedrooms: String(params.bedrooms) === String(n) ? "" : String(n) }); close(); }} className={`rounded-full border px-3.5 py-1.5 text-sm font-bold ${String(params.bedrooms) === String(n) ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-foreground/30"}`}>{n}+</button>
                ))}
              </div>
            </div>
          )}
        </ToolbarButton>

        <button
          type="button"
          onClick={onOpenFilters}
          className={`inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-3.5 text-[13px] font-bold transition-colors ${drawerFilterCount > 0 ? "border-primary/50 bg-primary/5 text-primary" : "border-border bg-background hover:border-foreground/30"}`}
          data-testid="filter-trigger"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          Filters
          {drawerFilterCount > 0 && <span className="grid h-4.5 min-w-[18px] place-items-center rounded-full bg-primary px-1 text-[10px] font-black text-primary-foreground">{drawerFilterCount}</span>}
        </button>

        <div className="ml-auto flex shrink-0 items-center gap-2">
          <button type="button" onClick={saveSearch} className="inline-flex h-9 items-center gap-1.5 rounded-full border border-border px-3.5 text-[13px] font-bold text-foreground hover:border-foreground/30" data-testid="save-search-btn" title="Save this search on this device">
            <Heart className="h-3.5 w-3.5" /> <span className="hidden md:inline">Save Search</span>
          </button>
          {showViewToggle && <div className="hidden items-center rounded-full border border-border p-0.5 lg:flex" role="group" aria-label="Layout" data-testid="view-toggle">
            {viewOptions.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.v}
                  type="button"
                  onClick={() => onViewChange(option.v)}
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-bold transition ${view === option.v ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid={`view-toggle-${option.v}`}
                  aria-pressed={view === option.v}
                >
                  <Icon className="h-3.5 w-3.5" /> {option.l}
                </button>
              );
            })}
          </div>}
        </div>
      </div>
    </div>
  );
}
